/**
 * KYB Utilities Class
 * Centralizes business logic and configuration for KYB checks
 */
export class KybUtils {
  /**
   * Check visibility modes
   */
  static VISIBILITY_MODE = {
    SHOW_ONLY: 'SHOW_ONLY',           // Show only to these emails
    SHOW_ALL_EXCEPT: 'SHOW_ALL_EXCEPT' // Show to everyone except these emails
  }

  /**
   * Email whitelist configuration for restricted checks
   * SHOW_ONLY: Only clients in this array can see the check
   * SHOW_ALL_EXCEPT: Everyone except clients in this array can see the check
   */
  static CHECK_RESTRICTIONS = {
    jap_jus: {
      mode: 'SHOW_ONLY',
      emails: ['starkbank@gueno.com', 'superadmin@gueno.com']
    },
    amldd: {
      mode: 'SHOW_ONLY',
      emails: ['starkbank@gueno.com', 'superadmin@gueno.com']
    }
    // Example of SHOW_ALL_EXCEPT:
    // someCheck: {
    //   mode: 'SHOW_ALL_EXCEPT',
    //   emails: ['restricted@example.com']
    // }
  }

  /**
   * Configuration for checks that should be disabled by default in creation forms
   * These checks will be visible but unchecked when creating a new KYB/KYC
   */
  static CHECKS_DISABLED_BY_DEFAULT = {
    amldd: true,      // AMLDD checks disabled by default
    jap_jus: false    // JAP_JUS checks enabled by default
  }

  /**
   * Configuration for checks that should be completely hidden
   * These checks will not appear anywhere in the UI
   * Use the check KEY (uppercase) to disable specific checks
   */
  static CHECKS_HIDDEN = [
    // Example: 'AMLDD_MTLSN_SIMPLE',  // Uncomment to hide MTLSN Simple
    // Example: 'AMLDD_ESG',             // Uncomment to hide ESG
    // 'AMLDD_MTLSN_WITH_NEWS',
  ]

  /**
   * JAP JUS check types (JusBrasil checks)
   */
  static JAP_JUS_CHECK_TYPES = ['jap_jus']

  /**
   * JAP JUS check keys (used in API requests)
   */
  static JAP_JUS_CHECK_KEYS = ['JUDICIAL_ADMINISTRATIVE_PROCESSES_JUS']

  /**
   * AMLDD check types (camelCase names used in the UI)
   * All check names that belong to AMLDD (Anti-Money Laundering Due Diligence)
   */
  static AMLDD_CHECK_TYPES = [
    'amlddEsg',
    'amlddLri',
    'amlddPep',
    'amlddMtlsn',
    'amlddMtlsnSimple',
    'amlddMtlsnWithNews'
  ]

  /**
   * AMLDD check keys (UPPERCASE names used in API requests)
   */
  static AMLDD_CHECK_KEYS = [
    'AMLDD_ESG',
    'AMLDD_LRI',
    'AMLDD_PEP',
    'AMLDD_MTLSN',
    'AMLDD_MTLSN_SIMPLE',
    'AMLDD_MTLSN_WITH_NEWS'
  ]

  /**
   * Check if a client can view a specific check based on email restrictions
   * @param {string} checkName - The check name (e.g., 'jap_jus', 'amlddEsg')
   * @param {string} emailClient - The client's email
   * @param {string} restrictionType - The restriction key to check ('jap_jus' or 'amldd')
   * @returns {boolean} - True if the client can view the check
   */
  static canViewCheck(checkName, emailClient, restrictionType) {
    if (!restrictionType || !this.CHECK_RESTRICTIONS[restrictionType]) {
      // No restriction configured, allow everyone
      return true
    }

    const restriction = this.CHECK_RESTRICTIONS[restrictionType]
    const { mode, emails } = restriction

    if (mode === this.VISIBILITY_MODE.SHOW_ONLY) {
      // Only show to emails in the list
      return emails.includes(emailClient)
    }

    if (mode === this.VISIBILITY_MODE.SHOW_ALL_EXCEPT) {
      // Show to everyone except emails in the list
      return !emails.includes(emailClient)
    }

    // Default: allow everyone
    return true
  }

  /**
   * Check if a check name is a JAP JUS check type
   * @param {string} checkName - The check name to verify
   * @returns {boolean} - True if it's a JAP JUS check
   */
  static isJapJusCheck(checkName) {
    if (!checkName) return false
    return this.JAP_JUS_CHECK_TYPES.includes(checkName)
  }

  /**
   * Check if a check key is a JAP JUS check key
   * @param {string} checkKey - The check key to verify
   * @returns {boolean} - True if it's a JAP JUS check key
   */
  static isJapJusCheckKey(checkKey) {
    if (!checkKey) return false
    return this.JAP_JUS_CHECK_KEYS.includes(checkKey)
  }

  /**
   * Check if a check name is an AMLDD check type
   * @param {string} checkName - The check name to verify
   * @returns {boolean} - True if it's an AMLDD check
   */
  static isAmlddCheck(checkName) {
    if (!checkName) return false
    return this.AMLDD_CHECK_TYPES.includes(checkName)
  }

  /**
   * Check if a check key is an AMLDD check key
   * @param {string} checkKey - The check key to verify (e.g., 'AMLDD_ESG')
   * @returns {boolean} - True if it's an AMLDD check key
   */
  static isAmlddCheckKey(checkKey) {
    if (!checkKey) return false
    return this.AMLDD_CHECK_KEYS.includes(checkKey)
  }

  /**
   * Get restriction configuration for a specific check type
   * @param {string} checkType - The check type ('jap_jus' or 'amldd')
   * @returns {Object|null} - Restriction configuration or null if not found
   */
  static getCheckRestriction(checkType) {
    return this.CHECK_RESTRICTIONS[checkType] || null
  }

  /**
   * Check if a client can view Nuclea checks
   * @param {boolean} isNuclea - Whether the user master has Nuclea enabled
   * @returns {boolean} - True if the client can view Nuclea checks
   */
  static canViewNucleaChecks(isNuclea) {
    return !!isNuclea
  }

  /**
   * Check if a check should be disabled by default in creation forms
   * @param {string} checkKey - The check key to verify (e.g., 'AMLDD_ESG', 'JUDICIAL_ADMINISTRATIVE_PROCESSES_JUS')
   * @returns {boolean} - True if the check should be disabled by default
   */
  static isCheckDisabledByDefault(checkKey) {
    if (!checkKey) return false

    // Check if it's an AMLDD check
    if (this.isAmlddCheckKey(checkKey)) {
      return this.CHECKS_DISABLED_BY_DEFAULT.amldd === true
    }

    // Check if it's a JAP_JUS check
    if (this.isJapJusCheckKey(checkKey)) {
      return this.CHECKS_DISABLED_BY_DEFAULT.jap_jus === true
    }

    return false
  }

  /**
   * Check if a check should be completely hidden from the UI
   * @param {string} checkKey - The check key to verify (e.g., 'AMLDD_ESG', 'AMLDD_MTLSN_SIMPLE')
   * @returns {boolean} - True if the check should be hidden
   */
  static isCheckHidden(checkKey) {
    if (!checkKey) return false
    return this.CHECKS_HIDDEN.includes(checkKey)
  }

  /**
   * Filter checks based on visibility restrictions
   * @param {Array} checks - Array of check objects
   * @param {string} emailClient - The client's email
   * @param {boolean} isNuclea - Whether Nuclea is enabled
   * @returns {Array} - Filtered array of checks
   */
  static filterVisibleChecks(checks, emailClient, isNuclea = false) {
    return checks.filter((kybCheck) => {
      // Filter hidden checks (priority check)
      if (this.isCheckHidden(kybCheck.key)) {
        return false
      }

      // Filter Nuclea checks
      if (kybCheck.section === 'nuclea' && !this.canViewNucleaChecks(isNuclea)) {
        return false
      }

      // Filter JAP JUS checks
      if (kybCheck.name === 'jap_jus' && !this.canViewCheck(kybCheck.name, emailClient, 'jap_jus')) {
        return false
      }

      // Filter AMLDD checks
      if (this.isAmlddCheck(kybCheck.name) && !this.canViewCheck(kybCheck.name, emailClient, 'amldd')) {
        return false
      }

      return true
    })
  }

  /**
   * Deep get a value from an object using a path with dot notation
   * Supports array notation with brackets: 'foo.bar[0].baz'
   * @param {Object} obj - The object to traverse
   * @param {Array<string>} keys - Array of keys to traverse
   * @returns {*} - The value at the path, or null if not found
   */
  static deepGet(obj, keys) {
    return keys.reduce((xs, x) => xs?.[x] ?? null, obj)
  }

  /**
   * Deep get a value from an object using a string path
   * Converts path string to array and supports array notation
   * Example: deepGetByPath(obj, 'foo.bar[0].baz') or deepGetByPath(obj, 'foo.bar.baz')
   * @param {Object} obj - The object to traverse
   * @param {string} path - The path string to traverse
   * @returns {*} - The value at the path, or null if not found
   */
  static deepGetByPath(obj, path) {
    return this.deepGet(
      obj,
      path
        .replace(/\[([^[\]]*)\]/g, '.$1.')
        .split('.')
        .filter((t) => t !== '')
    )
  }

  /**
   * Get short button label for check titles
   * Extracts abbreviations from long check titles for button display
   * Examples (AMLDD checks):
   *   "ESG (Environmental, Social and Governance Lists)" -> "AMLDD ESG"
   *   "LRI (International Restrictive Lists - OFAC, UN, EU, Interpol)" -> "AMLDD LRI"
   *   "PEP (Politically Exposed Persons)" -> "AMLDD PEP"
   *   "MTLSN (Media, Terrorism, Lawsuits, Sanctions, Negative)" -> "AMLDD MTLSN"
   *   "MTLSN Simple (Media, Terrorism, Lawsuits, Sanctions, Negative - Simplified)" -> "AMLDD MTLSN Simple"
   *   "MTLSN with News (Media, Terrorism, Lawsuits, Sanctions, Negative - With News)" -> "AMLDD MTLSN with News"
   * Examples (non-AMLDD checks):
   *   "CNEP (National Registry of Legal Entities)" -> "CNEP"
   *   "CDN (Customer Due Diligence)" -> "CDN"
   * @param {string} fullTitle - The full check title
   * @param {string} checkKey - Optional check key to determine if it's an AMLDD check
   * @returns {string} - The abbreviated title for button display
   */
  static getShortCheckLabel(fullTitle, checkKey = null) {
    if (!fullTitle) return ''

    // Check if this is an AMLDD check by examining the checkKey
    const isAmldd = checkKey ? this.isAmlddCheckKey(checkKey) : false

    // Special handling for MTLSN variants - extract everything before the parenthesis
    if (fullTitle.includes('MTLSN')) {
      const mtlsnMatch = fullTitle.match(/^(MTLSN[^(]*?)\s*\(/)
      if (mtlsnMatch) {
        const mtlsnLabel = mtlsnMatch[1].trim()
        return isAmldd ? `AMLDD ${mtlsnLabel}` : mtlsnLabel
      }
    }

    // Extract text before first parenthesis
    const match = fullTitle.match(/^([A-Z]+)\s*\(/)
    if (match) {
      const abbreviation = match[1].trim()
      return isAmldd ? `AMLDD ${abbreviation}` : abbreviation
    }

    // If no match, return the full title
    return fullTitle
  }

  /**
   * Map check key (UPPERCASE) to translation key (camelCase)
   * Used for translating timeline descriptions from backend check keys
   * @param {string} checkKey - The check key in UPPERCASE (e.g., 'AMLDD_ESG', 'AMLDD_MTLSN_WITH_NEWS')
   * @returns {string} - The translation key in camelCase (e.g., 'amlddEsg', 'amlddMtlsnWithNews')
   */
  static getTranslationKeyFromCheckKey(checkKey) {
    if (!checkKey) return checkKey

    // Map of special cases (UPPERCASE key -> camelCase translation key)
    const keyMap = {
      // AMLDD checks
      'AMLDD_ESG': 'amlddEsg',
      'AMLDD_LRI': 'amlddLri',
      'AMLDD_PEP': 'amlddPep',
      'AMLDD_MTLSN': 'amlddMtlsn',
      'AMLDD_MTLSN_SIMPLE': 'amlddMtlsnSimple',
      'AMLDD_MTLSN_WITH_NEWS': 'amlddMtlsnWithNews',
      // JAP JUS check
      'JUDICIAL_ADMINISTRATIVE_PROCESSES_JUS': 'judicialAdministrativeProcessesJusBrasil',
      // Add more mappings as needed
    }

    return keyMap[checkKey] || checkKey
  }
}
